import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from '@tanstack/react-router';
import { Route } from '@/routes/_authenticated/setup/payors';
import { Plus, ChevronDown, ChevronRight, RotateCcw } from 'lucide-react';
import type { AcceptedPayorWithNames, PayorPlanResponse } from '@vagusx/clients';
import { SortableHeader } from '@/components/table-page';
import { DataTable } from '@/components/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Tabs, TabsTrigger } from '@/components/ui/tabs';
import { PageTabsList } from '@/components/custom/page-tabs-list';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useAcceptedPayors,
  usePayorPlans,
  useAllAcceptedPayorPlans,
  useReactivatePayor,
} from '@/hooks/use-payors';
import { AcceptedPayorEditor } from './AcceptedPayorEditor';
import { AcceptedPayorDetail } from './AcceptedPayorDetail';

// ==========================================================================
// Manage tab types
// ==========================================================================

interface PayorGroup {
  payorId: string;
  payorName: string;
  acceptedPayorId: string;
  hasAllPlans: boolean;
  planNames: string[];
  isActive: boolean;
}

// ==========================================================================
// Plans tab types + columns
// ==========================================================================

interface PayorPlanRow {
  acceptedPayorId: string;
  payorId: string;
  payorName: string;
  planName: string;
  planType: string;
}

const planColumns: ColumnDef<PayorPlanRow>[] = [
  {
    accessorKey: 'payorName',
    header: ({ column }) => <SortableHeader column={column}>Payor</SortableHeader>,
    cell: ({ row }) => <span className="font-medium">{row.original.payorName}</span>,
  },
  {
    accessorKey: 'planName',
    header: ({ column }) => <SortableHeader column={column}>Plan</SortableHeader>,
    cell: ({ row }) => <span className="text-muted-foreground">{row.original.planName}</span>,
  },
  {
    accessorKey: 'planType',
    header: ({ column }) => <SortableHeader column={column}>Type</SortableHeader>,
    cell: ({ row }) => (
      <span className="text-xs text-muted-foreground">{row.original.planType}</span>
    ),
  },
];

// ==========================================================================
// Main page
// ==========================================================================

export default function PayorsPage(): React.ReactElement {
  const { id: selectedId } = Route.useSearch();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [tab, setTab] = useState<'manage' | 'plans'>('manage');
  const [collapsedPayors, setCollapsedPayors] = useState<Set<string>>(new Set());
  const [showInactive, setShowInactive] = useState(false);

  const { data: acceptedPayors, isLoading, error } = useAcceptedPayors(showInactive);

  // Group by payor for Manage tab
  const payorGroups = useMemo((): PayorGroup[] => {
    if (!acceptedPayors) return [];
    const map = new Map<string, AcceptedPayorWithNames[]>();
    for (const ap of acceptedPayors) {
      if (!map.has(ap.payorId)) map.set(ap.payorId, []);
      map.get(ap.payorId)!.push(ap);
    }
    return Array.from(map.entries()).map(([payorId, rows]) => ({
      payorId,
      payorName: rows[0]!.payorName,
      acceptedPayorId: rows[0]!.id,
      hasAllPlans: rows.some((r) => !r.payorPlanId),
      planNames: rows.filter((r) => r.planName).map((r) => r.planName!),
      isActive: rows.some((r) => r.rowStatus === 0),
    }));
  }, [acceptedPayors]);

  // For Plans tab: expand "all plans" into individual rows
  const allPlansPayorIds = useMemo(
    () => (acceptedPayors ?? []).filter((ap) => !ap.payorPlanId).map((ap) => ap.payorId),
    [acceptedPayors]
  );
  const { data: catalogPlansMap } = useAllAcceptedPayorPlans(allPlansPayorIds);

  const flatRows = useMemo((): PayorPlanRow[] => {
    if (!acceptedPayors) return [];
    const rows: PayorPlanRow[] = [];
    for (const ap of acceptedPayors) {
      if (ap.payorPlanId) {
        rows.push({
          acceptedPayorId: ap.id,
          payorId: ap.payorId,
          payorName: ap.payorName,
          planName: ap.planName ?? 'Unknown',
          planType: '',
        });
      } else {
        const plans = catalogPlansMap?.get(ap.payorId);
        if (plans && plans.length > 0) {
          for (const plan of plans) {
            rows.push({
              acceptedPayorId: ap.id,
              payorId: ap.payorId,
              payorName: ap.payorName,
              planName: plan.name,
              planType: plan.planType,
            });
          }
        } else {
          rows.push({
            acceptedPayorId: ap.id,
            payorId: ap.payorId,
            payorName: ap.payorName,
            planName: 'All plans',
            planType: '',
          });
        }
      }
    }

    // Deduplicate by payorId + planName (multiple "all plans" rows expand to same plans)
    const seen = new Set<string>();
    return rows.filter((r) => {
      const key = `${r.payorId}:${r.planName}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [acceptedPayors, catalogPlansMap]);

  const toggleExpand = (payorId: string): void => {
    setCollapsedPayors((prev) => {
      const next = new Set(prev);
      if (next.has(payorId)) next.delete(payorId);
      else next.add(payorId);
      return next;
    });
  };

  const handlePayorClick = (group: PayorGroup): void => {
    navigate({ to: '/setup/payors', search: { id: group.acceptedPayorId }, replace: true });
  };

  const handleCloseSheet = (): void => {
    navigate({ to: '/setup/payors', search: {}, replace: true });
    setIsCreating(false);
  };

  const payorCount = payorGroups.length;

  return (
    <div className="flex h-full min-h-0 flex-col p-6">
      {/* Header */}
      <div className="mb-4 flex shrink-0 items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">
            Payors
            {!isLoading && payorCount > 0 && (
              <span className="ml-1 text-lg font-normal text-muted-foreground">({payorCount})</span>
            )}
          </h1>
          <p className="text-muted-foreground">
            {payorCount > 0
              ? `${payorCount} payor${payorCount !== 1 ? 's' : ''}, ${flatRows.length} plan${flatRows.length !== 1 ? 's' : ''}`
              : 'Manage insurance payors and accepted plans'}
          </p>
        </div>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Payor
        </Button>
      </div>

      {/* Tabs + Show inactive */}
      <div className="mb-4 flex shrink-0 items-center justify-between gap-4">
        <Tabs value={tab} onValueChange={(v) => setTab(v as 'manage' | 'plans')} className="flex-1">
          <PageTabsList>
            <TabsTrigger value="manage">Manage</TabsTrigger>
            <TabsTrigger value="plans">Plans</TabsTrigger>
          </PageTabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <Switch
            id="showInactive"
            checked={showInactive}
            onCheckedChange={setShowInactive}
            aria-label="Show inactive payors"
          />
          <Label htmlFor="showInactive" className="text-sm text-muted-foreground">
            Show inactive
          </Label>
        </div>
      </div>

      {/* Manage tab */}
      {tab === 'manage' && (
        <div className="min-h-0 flex-1 overflow-y-auto rounded-md border">
          {isLoading && (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          )}

          {error && (
            <div className="flex h-64 items-center justify-center">
              <p className="text-destructive">Failed to load payors.</p>
            </div>
          )}

          {!isLoading && !error && payorGroups.length === 0 && (
            <div className="flex h-64 items-center justify-center">
              <p className="text-muted-foreground">
                No payors configured. Click "Add Payor" to get started.
              </p>
            </div>
          )}

          {!isLoading && !error && payorGroups.length > 0 && (
            <div className="divide-y">
              {payorGroups.map((group) => (
                <ManagePayorRow
                  key={group.payorId}
                  group={group}
                  isExpanded={!collapsedPayors.has(group.payorId)}
                  onToggleExpand={() => toggleExpand(group.payorId)}
                  onEdit={() => handlePayorClick(group)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Plans tab */}
      {tab === 'plans' && (
        <div className="min-h-0 flex-1">
          {error ? (
            <div className="flex h-64 items-center justify-center rounded-md border">
              <p className="text-destructive">Failed to load plans.</p>
            </div>
          ) : (
            <DataTable
              columns={planColumns}
              data={flatRows}
              isLoading={isLoading}
              searchColumn="planName"
              searchPlaceholder="Search plans..."
              emptyMessage="No plans to show."
              fillHeight
            />
          )}
        </div>
      )}

      {/* Add Payor Sheet */}
      <Sheet open={isCreating} onOpenChange={(open) => !open && setIsCreating(false)}>
        <SheetContent className="w-[520px] sm:max-w-[520px]">
          <AcceptedPayorEditor onClose={() => setIsCreating(false)} />
        </SheetContent>
      </Sheet>

      {/* Payor Detail Sheet */}
      <Sheet
        open={!!selectedId && !isCreating}
        onOpenChange={(open) => !open && handleCloseSheet()}
      >
        <SheetContent className="w-[520px] sm:max-w-[520px]">
          {selectedId && (
            <AcceptedPayorDetail acceptedPayorId={selectedId} onClose={handleCloseSheet} />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ==========================================================================
// Manage tab row component
// ==========================================================================

function ManagePayorRow({
  group,
  isExpanded,
  onToggleExpand,
  onEdit,
}: {
  group: PayorGroup;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
}): React.ReactElement {
  const { data: catalogPlans } = usePayorPlans(group.hasAllPlans ? group.payorId : undefined);
  const reactivate = useReactivatePayor();
  const plans = group.hasAllPlans
    ? (catalogPlans?.map((p) => ({ id: p.id, name: p.name })) ?? [])
    : group.planNames.map((name, i) => ({ id: `plan-${i}-${name}`, name }));
  const count = plans.length;
  const inactive = !group.isActive;

  return (
    <div className={inactive ? 'opacity-60' : ''}>
      {/* Payor header */}
      <div className="flex items-center hover:bg-accent/50">
        <button
          type="button"
          onClick={onToggleExpand}
          className="flex w-8 shrink-0 items-center justify-center px-1 py-3 text-muted-foreground hover:text-foreground"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {inactive ? (
          <div className="flex flex-1 items-center py-3 pr-4">
            <div className="flex flex-1 items-center gap-2">
              <span className="text-sm font-medium">{group.payorName}</span>
              <Badge variant="outline" className="text-[10px]">
                Inactive
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => reactivate.mutate(group.payorId)}
              disabled={reactivate.isPending}
            >
              <RotateCcw className="mr-1 h-3 w-3" />
              Reactivate
            </Button>
          </div>
        ) : (
          <div
            role="button"
            tabIndex={0}
            onClick={onEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onEdit();
              }
            }}
            className="flex flex-1 cursor-pointer items-center py-3 pr-4 text-left"
          >
            <span className="flex-1 text-sm font-medium">{group.payorName}</span>
            <span className="text-sm text-muted-foreground">
              {group.hasAllPlans ? (
                <Badge variant="secondary">All {count > 0 ? count : ''} plans</Badge>
              ) : (
                `${count} plan${count !== 1 ? 's' : ''}`
              )}
            </span>
          </div>
        )}
      </div>

      {/* Plan rows */}
      {isExpanded && plans.length > 0 && (
        <div className="bg-muted/30">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className="border-t border-border/50 py-2 pl-12 pr-4 text-sm text-muted-foreground"
            >
              {plan.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
